import { FormulaOperator } from '@doss/prisma/types'

import { getFormulaOperatorSpec } from './formula-operator-specs.js'
import {
  getFormulaKeyword,
  isColumnFormulaOperator,
  isRowFormulaOperator,
  type ColumnFormulaOperator,
  type RowFormulaOperator,
} from './formula.js'
import {
  FormulaConditionJson,
  FormulaJson,
  FormulaValueJson,
  TableColumnJson,
} from './types/formula.js'
import { LogicExpressionJson, LogicType } from './types/logic.js'
import { DossDataFormat, isTableCellData, TableCellData } from './types/table-cell.js'

// Prints a formula's surface syntax from its structured tree — the inverse of the parser's
// model builder. Some programmatic creation paths never wrote Formula.formulaString, so the
// tree is the only faithful source for them. Output is the grammar's canonical spelling with
// `{uuid}` references; printed text must re-parse, which the round-trip tests pin.
//
// Completeness is static: `printPrintableOperator` switches over the platform's own operator
// vocabulary (RowFormulaOperator | ColumnFormulaOperator) and its default arm is
// `satisfies never` — adding an operator to the vocabulary breaks this module's compile until
// the printer spells it (or `printFormulaExpr` learns to refuse it). Argument shapes come from
// `getFormulaOperatorSpec`, the same table the parser validates against.

/** The tree uses a shape the surface grammar cannot spell (date literals, form refs, …). */
export class FormulaPrintError extends Error {}

/** Every operator the printer can spell: the platform's value-producing vocabulary. */
export type PrintableFormulaOperator = RowFormulaOperator | ColumnFormulaOperator

const ARITHMETIC_SYMBOLS: Partial<Record<FormulaOperator, string>> = {
  [FormulaOperator.ADDITION]: '+',
  [FormulaOperator.SUBTRACTION]: '-',
  [FormulaOperator.MULTIPLICATION]: '*',
  [FormulaOperator.DIVISION]: '/',
}

function numberLiteral(value: number): string {
  const text = String(value)
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    throw new FormulaPrintError(`number ${text} has no formula-literal spelling`)
  }
  return text
}

function stringLiteral(value: string): string {
  if (/[\r\n]/.test(value)) {
    throw new FormulaPrintError('string literals cannot contain line breaks')
  }
  return `"${value.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`
}

function cellDataLiteral(data: TableCellData): string {
  switch (data.type) {
    case DossDataFormat.NUMBER:
      return numberLiteral(data.data)
    case DossDataFormat.STRING:
    case DossDataFormat.ENUM:
      return stringLiteral(String(data.data))
    case DossDataFormat.BOOLEAN:
      return data.data ? 'TRUE' : 'FALSE'
    case DossDataFormat.ARRAY: {
      const items = data.data
      if (!Array.isArray(items) || items.length === 0) {
        throw new FormulaPrintError('value lists must have at least one item')
      }
      const printed = items.map((item) => {
        if (typeof item === 'number') {
          return numberLiteral(item)
        }
        if (typeof item === 'string') {
          return stringLiteral(item)
        }
        throw new FormulaPrintError(`a ${typeof item} has no list-item spelling`)
      })
      return `(${printed.join(', ')})`
    }
    default:
      throw new FormulaPrintError(`a ${data.type} literal has no formula spelling`)
  }
}

type LogicOperand = Extract<LogicExpressionJson, { type: LogicType.COMPARISON }>['left']

function printOperand(operand: LogicOperand): string {
  if (isTableCellData(operand as Record<string, unknown>)) {
    return cellDataLiteral(operand as TableCellData)
  }
  if (typeof operand === 'object' && operand !== null && 'operator' in operand) {
    return printFormulaExpr(operand as FormulaJson)
  }
  if (typeof operand === 'object' && operand !== null && 'table' in operand) {
    return `{${(operand as TableColumnJson).id}}`
  }
  throw new FormulaPrintError('the operand references a form field or input — not a table value')
}

function printLogic(expression: LogicExpressionJson): string {
  switch (expression.type) {
    case LogicType.BOOLEAN:
      return expression.value ? 'TRUE' : 'FALSE'
    case LogicType.NOT:
      return `NOT (${printLogic(expression.expression)})`
    case LogicType.AND:
    case LogicType.OR: {
      if (expression.expressions.length === 0) {
        throw new FormulaPrintError('an AND/OR group must have at least one term')
      }
      const joiner = expression.type === LogicType.AND ? ' AND ' : ' OR '
      // Compound children are parenthesized so the printed precedence matches the tree.
      return expression.expressions
        .map((child) =>
          child.type === LogicType.AND || child.type === LogicType.OR
            ? `(${printLogic(child)})`
            : printLogic(child),
        )
        .join(joiner)
    }
    case LogicType.COMPARISON:
      return `${printOperand(expression.left)} ${expression.condition} ${printOperand(expression.right)}`
    case LogicType.UNARY:
      return `${printOperand(expression.value)} ${expression.condition}`
  }
}

function printValue(value: FormulaValueJson, parenthesizeFormula: boolean): string {
  if (value.conditions.length > 0) {
    return `IFS(${value.conditions.map(printConditionPair).join(', ')})`
  }
  if (value.formula !== undefined) {
    const printed = printFormulaExpr(value.formula)
    return parenthesizeFormula ? `(${printed})` : printed
  }
  if (value.tableColumn !== undefined && value.tableColumn !== null) {
    return `{${value.tableColumn.id}}`
  }
  const numberValue = value.valueNumber ?? value.value
  if (numberValue !== undefined && numberValue !== null) {
    return numberLiteral(numberValue)
  }
  if (value.valueString !== undefined && value.valueString !== null) {
    return stringLiteral(value.valueString)
  }
  throw new FormulaPrintError('a formula value carries neither a column, a literal, nor a branch')
}

function printConditionPair(condition: FormulaConditionJson): string {
  return `${printLogic(condition.condition)}, ${printFormulaExpr(condition.formula)}`
}

/** The values in tree order. Programmatic writers are the printer's whole audience, so the
 * stored `order` is authoritative — never the fetch order. */
function orderedValues(formula: FormulaJson): FormulaValueJson[] {
  return [...formula.formulaValues].sort((a, b) => a.order - b.order)
}

/** `KEYWORD(arg, …)` — the enum name is the grammar keyword for every function-call operator.
 * The argument count comes from the operator's own parser spec, so the shapes cannot drift. */
function printCall(operator: PrintableFormulaOperator, formula: FormulaJson): string {
  const values = orderedValues(formula)
  const spec = getFormulaOperatorSpec(operator)
  if (spec === undefined) {
    throw new FormulaPrintError(`${operator} has no operator spec`)
  }
  const args = spec.args
  switch (args.kind) {
    case 'fixed': {
      // `optionalFrom` marks the first omittable position (ROUND's digit count, TO_DATE's format),
      // so the printed call may carry anywhere from that many values up to the full position list.
      const max = args.positions.length
      const min = args.optionalFrom ?? max
      if (values.length < min || values.length > max) {
        const requirement =
          min === max ? `exactly ${max} value${max === 1 ? '' : 's'}` : `${min} to ${max} values`
        throw new FormulaPrintError(`${operator} takes ${requirement}`)
      }
      break
    }
    case 'variadic':
      if (values.length < args.min) {
        throw new FormulaPrintError(`${operator} takes at least ${args.min} value(s)`)
      }
      break
    case 'unitLiteral':
      // Base arguments plus the trailing calendar-unit string literal.
      if (values.length !== args.base.length + 1) {
        throw new FormulaPrintError(`${operator} takes exactly ${args.base.length + 1} values`)
      }
      break
    case 'arithmetic':
      throw new FormulaPrintError(`${operator} is not a function-call operator`)
  }
  return `${operator}(${values.map((value) => printValue(value, false)).join(', ')})`
}

/** Prints aggregations with the filtered spelling when a column-operation filter is present. */
function printAggregation(operator: ColumnFormulaOperator, formula: FormulaJson): string {
  const values = orderedValues(formula)
  const hasFilter = formula.columnOperationFilter !== undefined
  const keyword = getFormulaKeyword(operator, hasFilter)
  if (operator === FormulaOperator.SUMPRODUCT) {
    if (hasFilter) {
      throw new FormulaPrintError('SUMPRODUCT has no filtered spelling')
    }
    return printCall(operator, formula)
  }
  // Dual-mode operators are variadic in row mode; filtered forms take (condition, value).
  const isVariadic = !hasFilter && isRowFormulaOperator(operator)
  if (!isVariadic && values.length !== 1) {
    throw new FormulaPrintError(`${keyword} takes exactly one value`)
  }
  if (values.length === 0) {
    throw new FormulaPrintError(`${keyword} has no values`)
  }
  const printed = values.map((value) => printValue(value, false)).join(', ')
  if (hasFilter && formula.columnOperationFilter !== undefined) {
    return `${keyword}(${printLogic(formula.columnOperationFilter)}, ${printed})`
  }
  return `${keyword}(${printed})`
}

/** One arm per operator in the platform vocabulary — the `satisfies never` default is the
 * completeness guarantee: a new operator cannot compile until this switch spells it. */
function printPrintableOperator(operator: PrintableFormulaOperator, formula: FormulaJson): string {
  switch (operator) {
    case FormulaOperator.ADDITION:
    case FormulaOperator.SUBTRACTION:
    case FormulaOperator.MULTIPLICATION:
    case FormulaOperator.DIVISION: {
      const values = orderedValues(formula)
      if (values.length === 0) {
        throw new FormulaPrintError(`${operator} has no values`)
      }
      const symbol = ARITHMETIC_SYMBOLS[operator]
      if (symbol === undefined) {
        throw new FormulaPrintError(`${operator} has no infix symbol`)
      }
      // The platform parser dissolves SAME-operator parentheses into the parent chain. For the
      // associative operators that is harmless; under - and / it would change the value
      // (a - (b - c) ≠ a - b - c), so a tree the grammar cannot preserve REFUSES instead of
      // printing text that re-parses to a different computation. Head position is exempt:
      // (a - b) - c and the flat chain fold identically.
      const associative =
        operator === FormulaOperator.ADDITION || operator === FormulaOperator.MULTIPLICATION
      if (!associative) {
        for (const value of values.slice(1)) {
          if (value.formula?.operator === operator && value.conditions.length === 0) {
            throw new FormulaPrintError(
              `${operator} nested under ${operator} has no faithful spelling — the parser ` +
                'flattens same-operator parentheses',
            )
          }
        }
      }
      // Nested formulas parenthesize so the flat n-ary chain keeps the tree's grouping.
      return values.map((value) => printValue(value, true)).join(` ${symbol} `)
    }
    case FormulaOperator.SUM:
    case FormulaOperator.COUNT:
    case FormulaOperator.COUNT_DISTINCT:
    case FormulaOperator.AVERAGE:
    case FormulaOperator.SUMPRODUCT:
    case FormulaOperator.MIN:
    case FormulaOperator.MAX:
      return printAggregation(operator, formula)
    case FormulaOperator.ROUND:
    case FormulaOperator.CEIL:
    case FormulaOperator.FLOOR:
    case FormulaOperator.UPPER:
    case FormulaOperator.LOWER:
    case FormulaOperator.TRIM:
    case FormulaOperator.LEN:
    case FormulaOperator.CONCAT:
    case FormulaOperator.LEFT:
    case FormulaOperator.RIGHT:
    case FormulaOperator.MID:
    case FormulaOperator.SUBSTITUTE:
    case FormulaOperator.DATE:
    case FormulaOperator.DATEADD:
    case FormulaOperator.DATEDIFF:
    case FormulaOperator.YEAR:
    case FormulaOperator.MONTH:
    case FormulaOperator.DAY:
    case FormulaOperator.TO_TEXT:
    case FormulaOperator.TO_NUMBER:
    case FormulaOperator.TO_DATE:
      return printCall(operator, formula)
    default:
      return operator satisfies never
  }
}

function printFormulaExpr(formula: FormulaJson): string {
  const { operator } = formula
  if (!isRowFormulaOperator(operator) && !isColumnFormulaOperator(operator)) {
    throw new FormulaPrintError(`${operator} is not a value-producing operator`)
  }
  return printPrintableOperator(operator, formula)
}

/** The formula's surface syntax, printed from the structured tree with `{uuid}` references. */
export function printFormulaString(formula: FormulaJson): string {
  return printFormulaExpr(formula)
}

/**
 * A logic expression's surface syntax — the `logicalExpression` scope of the same grammar
 * (lookup filters, `LogicExpression.logicExpressionString`). The one library implementation:
 * the string backfill script consumes this instead of carrying its own copy.
 */
export function printLogicExpressionString(expression: LogicExpressionJson): string {
  return printLogic(expression)
}
